/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useState } from "react";

import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import { colors, SecurityLockedIcon } from "@dotnet/telematics-design-system";

import { useAuth } from "../../../../../../../context/Auth/AuthProvider";
import { useReleaseFeatureToggle } from "../../../../../../../shared/featureToggles/hooks";
import { FEATURE_NAMES } from "../../../../../../../shared/featureToggles/types";
import TextLink from "../../../../../../TextLink/TextLink";
import { TEXT_LINK_SIZES } from "../../../../../../TextLink/types";
import ContentBox from "../../../components/ContentBox/ContentBox";
import useTwoFA from "../../../hooks/useTwoFA";
import { useStyles } from "../../styles";
import DisableTwoFA from "./DisableTwoFA/DisableTwoFA";
import EnableTwoFA from "./EnableTwoFA/EnableTwoFA";

const TwoFA = () => {
  const { user } = useAuth();
  const { isReleaseFeatureEnabledForRole } = useReleaseFeatureToggle();
  const { enabledStatValues, disabledStatValues } = useTwoFA({});

  const { t } = useTranslation();

  const [isEnableDialogOpen, setIsEnableDialogOpen] = useState(false);
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);

  const isEnabled = user?.twoFactorEnabled;

  const { contentBoxInnerDiv, settingPlaceholder, twoFaIconCustomStyle } =
    useStyles({ colors });

  const isRoleAllowedToToggle2FA = isReleaseFeatureEnabledForRole(
    FEATURE_NAMES.ShowToggle2FA,
    user?.role
  );

  const valuesByStatus = isEnabled ? enabledStatValues : disabledStatValues;

  const openDialog = () =>
    isEnabled ? setIsDisableDialogOpen(true) : setIsEnableDialogOpen(true);

  const closeDialog = () => {
    setIsEnableDialogOpen(false);
    setIsDisableDialogOpen(false);
  };

  return (
    <>
      <ContentBox
        title={t("Password##two-factor authentication")}
        description={t("Password##two-factor authentication description")}
      >
        <div css={css(contentBoxInnerDiv)}>
          <div css={css(settingPlaceholder)}>
            <SecurityLockedIcon
              css={css(twoFaIconCustomStyle)}
              data-testid="2FA-icon"
            />

            <Typography variant="body2Medium">
              {t("Password##two-factor authentication status")}
            </Typography>

            {/* empty space */}
            <Typography variant="body2Medium">&nbsp;</Typography>

            <Typography
              data-testid="two-factor-status-string"
              variant="body2SemiBold"
              sx={{ color: valuesByStatus.color }}
            >
              {valuesByStatus.statusString}
            </Typography>
          </div>

          {(isRoleAllowedToToggle2FA ||
            (!isRoleAllowedToToggle2FA && !isEnabled)) && (
            <TextLink
              size={TEXT_LINK_SIZES.Normal}
              onClick={openDialog}
              data-testid="toggle-two-factor-status-link"
            >
              {valuesByStatus.statusLink}
            </TextLink>
          )}
        </div>
      </ContentBox>

      {/* if mandatory 2FA would be disabled in <ProtectedRoutes />, the line below would trigger optional 2FA enabling */}
      <EnableTwoFA onCloseDialog={closeDialog} isOpen={isEnableDialogOpen} />

      <DisableTwoFA onCloseDialog={closeDialog} isOpen={isDisableDialogOpen} />
    </>
  );
};

export default TwoFA;
